import React, { useEffect, useState } from 'react';
import QcrLoader from 'Components/Icons/QcrLoader';

// Shown in CaseBaseInfo.jsx while the AI calls are still running.
// The AI calls don't report real progress, so the gauge (QCR logo, 1c) is
// timed: it fills towards 95% over EXPECTED_MS (~40s, the usual time).
// If the results come back sooner, CaseBaseInfo swaps this out for the
// insights straight away. If it runs past 40s, the gauge creeps slowly
// toward 99% and a "please wait" line appears - it never claims 100%.
const STAGES = [
  "Reviewing your case details…",
  "Estimating your settlement range…",
  "Assessing litigation risks…",
  "Matching law firms for your case…",
  "Putting together your AI insights…",
];

const STAGE_INTERVAL_MS = 2800;
const EXPECTED_MS = 40000;
const TICK_MS = 250;

// eased so it moves quickly at first and slows near the end
const percentAt = (ms) => {
  if (ms <= EXPECTED_MS) {
    const x = ms / EXPECTED_MS;
    return 95 * (1 - Math.pow(1 - x, 1.6));
  }
  const extra = (ms - EXPECTED_MS) / 1000; // seconds past the limit
  return 95 + 4 * (1 - Math.exp(-extra / 30));
};

// stalled: the case page stopped checking (~3 min) and the insights still
// aren't ready - show a short note and a "Check again" button instead.
function AiInsightsLoading({ stalled = false, onRetry }) {
  const [stageIndex, setStageIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStageIndex((prev) => (prev + 1) % STAGES.length);
    }, STAGE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const start = Date.now();
    const timer = setInterval(() => setElapsed(Date.now() - start), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const percent = Math.floor(percentAt(elapsed));
  const overdue = elapsed > EXPECTED_MS;

  if (stalled) {
    return (
      <div className='w-full flex flex-col items-center justify-center py-12 lg:py-16 gap-3 font-plexsans text-center px-4' role='status'>
        <p className='m-0 font-semibold text-[15px] text-navy'>Your AI insights are taking longer than usual</p>
        <p className='m-0 max-w-[360px] text-[13px] leading-[1.55] text-navy/[.6]'>They're still being prepared. Check again in a minute.</p>
        {onRetry &&
          <button type='button' onClick={onRetry}
            className='mt-1 inline-flex items-center justify-center h-11 px-5 rounded-md border-0 bg-brass hover:bg-brassDark text-ivory font-semibold text-[13.5px] cursor-pointer'>
            Check again
          </button>}
      </div>
    );
  }

  return (
    <div className='w-full flex flex-col items-center justify-center py-12 lg:py-16 gap-3.5 font-plexsans' role='status' aria-live='polite'>
      <QcrLoader variant='progress' size={72} value={percent} label='Preparing AI insights' />
      <span className='font-plexmono font-medium text-[18px] leading-none text-navy tabular-nums'>{percent}%</span>
      <p key={stageIndex} className='m-0 text-[13px] text-navy/[.6] text-center animate-fade-in'>
        {STAGES[stageIndex]}
      </p>
      {overdue &&
        <p className='m-0 max-w-[320px] text-center font-plexmono font-medium text-[11.5px] leading-[1.5] tracking-[.08em] uppercase text-brass animate-q-fade motion-reduce:animate-none'>
          Taking a little longer than usual - please wait
        </p>}
    </div>
  );
}

export default AiInsightsLoading;
